import { api } from '../../services/api';

export const INTERN_ROLE_CODE = 'INTERN';

export interface EmployeeRoleOption {
    id: number;
    role_code: string;
    role_name: string;
}

const normalizeRoleCode = (value: unknown): string =>
    String(value ?? '').trim().toUpperCase();

const normalizeEmployeeRole = (role: any): EmployeeRoleOption | null => {
    const id = Number(role?.id);
    const roleCode = normalizeRoleCode(role?.role_code ?? role?.roleCode);
    const roleName = String(role?.role_name ?? role?.roleName ?? roleCode).trim();

    if (!Number.isFinite(id) || id <= 0 || !roleCode || !roleName) {
        return null;
    }

    return {
        id,
        role_code: roleCode,
        role_name: roleName,
    };
};

export const normalizeEmployeeRoles = (roles: any[]): EmployeeRoleOption[] =>
    roles
        .map(normalizeEmployeeRole)
        .filter((role): role is EmployeeRoleOption => Boolean(role))
        .sort((a, b) => a.role_name.localeCompare(b.role_name));

export const fetchEmployeeRoleOptions = async (): Promise<EmployeeRoleOption[]> => {
    const res = await api.get('/employees/roles');
    return normalizeEmployeeRoles(Array.isArray(res.data) ? res.data : []);
};

export const findInternRole = (roles: EmployeeRoleOption[]): EmployeeRoleOption | null =>
    roles.find((role) => normalizeRoleCode(role.role_code) === INTERN_ROLE_CODE) ?? null;

export const toEmployeeRoleSelectOptions = (roles: EmployeeRoleOption[]) =>
    roles.map((role) => ({
        value: role.id,
        label: `${role.role_name} (${role.role_code})`,
    }));

export const upsertEmployeeRoleOption = (
    roles: EmployeeRoleOption[],
    nextRole: EmployeeRoleOption,
): EmployeeRoleOption[] =>
    normalizeEmployeeRoles([
        ...roles.filter((role) => role.id !== nextRole.id && role.role_code !== nextRole.role_code),
        nextRole,
    ]);

export const ensureInternEmployeeRole = async (): Promise<EmployeeRoleOption> => {
    const existingRoles = await fetchEmployeeRoleOptions();
    const existingIntern = findInternRole(existingRoles);
    if (existingIntern) {
        return existingIntern;
    }

    try {
        await api.post('/organization/roles', {
            roleCode: INTERN_ROLE_CODE,
            roleName: '实习生',
            description: 'Intern employee role for roster eligibility rules.',
            canSchedule: true,
            allowedShiftCodes: null,
            defaultSkillLevel: null,
        });
    } catch (error: any) {
        if (error?.response?.status !== 409) {
            throw error;
        }
    }

    const refreshedRoles = await fetchEmployeeRoleOptions();
    const createdIntern = findInternRole(refreshedRoles);
    if (!createdIntern) {
        throw new Error('INTERN_ROLE_NOT_FOUND_AFTER_CREATE');
    }

    return createdIntern;
};
